// Objectifs du projet (migration 049) : ce qui compte pour l'entreprise ce
// trimestre — « augmenter la conversion », « réduire les appels au support ».
//
// Un insight peut servir un objectif (choix dans sa carte) : son score de
// priorité est alors multiplié (× 1,5 pour un objectif principal, × 1,2 pour
// un secondaire), et le calcul affiché le dit. Cinq objectifs au plus.
//
// Gérer est réservé aux admins et Responsables (le serveur le garantit, 403) ;
// les autres membres lisent les objectifs.
import { useId, useState, type FormEvent } from 'react';
import { useCreateObjective, useDeleteObjective, useObjectives, useUpdateObjective } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { ObjectiveImportance, ProjectObjective } from '../api/types';
import { Button, Chip, ErrorBanner, Eyebrow, Field, Note, Select, Spinner } from '../components/ui';
import { IMPORTANCES } from '../insights/labels';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const day = (iso: string) => iso.split('-').reverse().join('/');

export function ObjectivesSection({ projectId }: { projectId: string }) {
  const query = useObjectives(projectId);
  const objectives = query.data?.objectives ?? [];
  const canWrite = query.data?.canWrite ?? false;
  const max = query.data?.max ?? 5;

  return (
    <section className="zy-card opt-section" aria-labelledby="obj-title">
      <div className="opt-head">
        <div>
          <Eyebrow>Objectifs</Eyebrow>
          <h2 className="t-h3" id="obj-title">Ce qui compte pour l’entreprise</h2>
          <p className="card-lede">
            Les objectifs que la recherche doit servir. Un insight rattaché à un objectif monte dans les
            priorités : × 1,5 pour un objectif principal, × 1,2 pour un secondaire.
          </p>
        </div>
      </div>

      {query.isPending ? (
        <Spinner label="Chargement des objectifs…" />
      ) : query.isError ? (
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
      ) : (
        <>
          {objectives.length === 0 ? (
            <p className="zy-texte-muet">
              Aucun objectif pour l’instant : les priorités ne tiennent compte que des utilisateurs.
            </p>
          ) : (
            <ul className="tax-list">
              {objectives.map((o) => (
                <ObjectiveRow key={o.id} projectId={projectId} objective={o} canWrite={canWrite} />
              ))}
            </ul>
          )}

          {canWrite ? (
            objectives.length < max ? (
              <AddObjective projectId={projectId} />
            ) : (
              <p className="zy-texte-muet">{max} objectifs au plus : au-delà, tout devient prioritaire.</p>
            )
          ) : (
            <Note tone="lecture">
              Lecture seule — les objectifs sont gérés par les responsables du projet et les administrateurs.
            </Note>
          )}
        </>
      )}
    </section>
  );
}

function ObjectiveRow({ projectId, objective: o, canWrite }: { projectId: string; objective: ProjectObjective; canWrite: boolean }) {
  const update = useUpdateObjective(projectId);
  const remove = useDeleteObjective(projectId);
  const [confirm, setConfirm] = useState(false);
  const selectId = useId();
  const error = update.error ?? remove.error;

  return (
    <li className="tax-row">
      <div className="tax-row-head">
        <div>
          <strong>{o.title}</strong>
          <p className="zy-texte-muet">
            {[
              o.metric ? `Indicateur : ${o.metric}` : null,
              o.due ? `échéance ${day(o.due)}` : null,
              `${o.insight_count} insight${o.insight_count > 1 ? 's' : ''} le ser${o.insight_count > 1 ? 'vent' : 't'}`,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="tax-actions">
          {canWrite ? (
            <>
              <label className="sr-only" htmlFor={selectId}>Importance</label>
              <Select
                id={selectId}
                className="obj-importance"
                value={o.importance}
                disabled={update.isPending}
                onChange={(e) => update.mutate({ id: o.id, patch: { importance: e.target.value as ObjectiveImportance } })}
              >
                {IMPORTANCES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
              </Select>
              {confirm ? (
                <>
                  <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(o.id)}>Confirmer</Button>
                  <Button variant="ghost" onClick={() => setConfirm(false)}>Annuler</Button>
                </>
              ) : (
                <Button variant="link-danger" size="s" type="button" onClick={() => setConfirm(true)}>Supprimer</Button>
              )}
            </>
          ) : (
            <Chip>{IMPORTANCES.find((i) => i.value === o.importance)?.label}</Chip>
          )}
        </div>
      </div>
      {confirm ? (
        <p className="zy-texte-muet">
          Les {o.insight_count} insight{o.insight_count > 1 ? 's' : ''} qui le servent restent, sans objectif.
        </p>
      ) : null}
      {error ? <ErrorBanner>{errorMessage(error)}</ErrorBanner> : null}
    </li>
  );
}

function AddObjective({ projectId }: { projectId: string }) {
  const create = useCreateObjective(projectId);
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState('');
  const [due, setDue] = useState('');
  const [importance, setImportance] = useState<ObjectiveImportance>('principal');
  const selectId = useId();

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({ title: title.trim(), metric: metric.trim(), due, importance });
      setTitle('');
      setMetric('');
      setDue('');
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <div className="tax-add">
      <p className="opt-subtitle">Ajouter un objectif</p>
      <form className="tax-form" onSubmit={submit} noValidate>
        <Field label="Objectif *" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Augmenter la conversion vers l’offre payante" maxLength={200} />
        <div className="tax-form-row">
          <Field label="Indicateur" value={metric} onChange={(e) => setMetric(e.target.value)}
            placeholder="Taux de passage à l’offre payante" maxLength={160} />
          <Field label="Échéance" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          <div className="zy-field">
            <label className="zy-field__label" htmlFor={selectId}>Importance</label>
            <Select id={selectId} value={importance}
              onChange={(e) => setImportance(e.target.value as ObjectiveImportance)}>
              {IMPORTANCES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
            </Select>
          </div>
        </div>
        {create.error ? <ErrorBanner>{errorMessage(create.error)}</ErrorBanner> : null}
        <div>
          <Button variant="accent" type="submit" loading={create.isPending} disabled={!title.trim()}>Ajouter l’objectif</Button>
        </div>
      </form>
    </div>
  );
}
